import apiClient from './apiClient';

// Interfaces for API responses
/**
 * Freelance Dashboard Types
 * @userstory TD-01, TD-08, TD-11, TD-14, TD-18, TD-21, TD-22
 */

export interface FreelanceFinancials {
	revenueToDate: number;
	effectiveRate: number;
	projectedAnnual: number;
	purchasedHours: number;
	usedHours: number;
	currency: string;
}

export interface FreelanceRevenueItem {
	month: string;
	hours: number;
	revenue: number;
}

export interface FreelanceRevenue {
	items: FreelanceRevenueItem[];
}

export interface FreelanceMilestone {
	project: string;
	title: string;
	deadline: string;
	status: string;
	estimatedHours: number;
	revenue: number;
}

export interface FreelanceTimesheet {
	month: string;
	hours: number;
	status: string;
}

/**
 * NEW: Freelance Onboarding & Goal Types
 */

export interface TalentOnboardingStatus {
	hasSkills: boolean;
	hasHourlyRate: boolean;
	hasPortfolio: boolean;
	hasBillingInfo: boolean;
	hasExperience: boolean;
	totalAllies: number;
	dreamTeamCount: number;
}

export interface TalentYearlyGoal {
	year: number;
	earnedAmount: number;
	confirmedAmount: number;
	totalBillableHours?: number;
	averageHourlyRate?: number;
	goals?: GoalWithProgress[];
	revenueGoal: number | null;
	billableHoursGoal?: number | null;
	averageHourlyRateGoal?: number | null;
}

export interface GoalWithProgress {
	goalType: 'Revenue' | 'BillableHours' | 'AverageHourlyRate';
	targetValue: number;
	currentValue: number;
}

export interface SetAllYearlyGoalsRequest {
	year?: number;
	revenueTarget?: number | null;
	billableHoursTarget?: number | null;
	averageHourlyRateTarget?: number | null;
}

export interface TalentQuickStats {
	activeContracts: number;
	pendingQuoteRequests: number;
	unreadMessages: number;
}

/**
 * User experience level based on platform activity.
 */
export type UserExperienceLevel = 'New' | 'Active' | 'Veteran';

/**
 * Dashboard context for freelance users - determines layout and experience.
 */
export interface TalentDashboardContext {
	experienceLevel: UserExperienceLevel;
	isProfileComplete: boolean;
	hasContracts: boolean;
	showOnboarding: boolean;
	showAdvancedDashboard: boolean;
	completedContractsCount: number;
}

/**
 * NEW: Company Onboarding & Overview Types
 */

export interface CompanyOnboardingStatus {
	hasOrganization: boolean;
	hasCompleteProfile: boolean;
	hasSearchedTalent: boolean;
	hasSentQuoteRequest: boolean;
	hasSignedQuote: boolean;
}

export interface CompanyTeamOverview {
	activeTalents: number;
	activeProjects: number;
	pendingQuotes: number;
}

export interface CompanyPendingActions {
	quotesToEvaluate: number;
	contractsToSign: number;
	timesheetsToApprove: number;
}

export interface CompanyQuickStats {
	activeTalents: number;
	activeProjects: number;
	pendingQuotes: number;
}

/**
 * Dashboard context for company users - determines layout and experience.
 */
export interface CompanyDashboardContext {
	isProfileComplete: boolean;
	hasProjects: boolean;
	hasContracts: boolean;
	showOnboarding: boolean;
	showAdvancedDashboard: boolean;
	activeProjectsCount: number;
	activeTalentsCount: number;
}

// =====================================================================
// COMPANY ONBOARDING JOURNEY & EMPLOYER SCORE TYPES
// =====================================================================

/**
 * Company onboarding journey status - 7 key milestones showing end-to-end platform value
 */
export interface CompanyOnboardingJourney {
	// Step 1: Profile complete
	hasCompleteProfile: boolean;
	// Step 2: Saved search
	hasSavedSearch: boolean;
	firstSavedSearchAt?: string;
	// Step 3: Contract type configured
	hasContractType: boolean;
	firstContractTypeAt?: string;
	// Step 4: Signed offer
	hasSignedOffer: boolean;
	firstSignedOfferAt?: string;
	firstSignedOfferTalentName?: string;
	// Step 5: Milestone approved
	hasApprovedMilestone: boolean;
	firstMilestoneApprovedAt?: string;
	// Step 6: Timesheet approved
	hasApprovedTimesheet: boolean;
	firstTimesheetApprovedAt?: string;
	// Step 7: Invoice paid
	hasPaidInvoice: boolean;
	firstInvoicePaidAt?: string;
	// Computed
	completedSteps: number;
	totalSteps: number;
	completionPercent: number;
	currentStep: number;
}

/**
 * Event in the company timeline
 */
export interface CompanyTimelineEvent {
	eventType: string;
	eventDate: string;
	relatedEntityName?: string;
}

/**
 * Company timeline with story and aggregated stats
 */
export interface CompanyTimeline {
	events: CompanyTimelineEvent[];
	monthsOnPlatform: number;
	totalTalentsCollaborated: number;
	totalProjectsCompleted: number;
	totalInvested: number;
}

/**
 * Company employer score - calculated reputation based on behavior
 */
export interface CompanyEmployerScore {
	score: number;
	tier: 'Building' | 'Fair' | 'Good' | 'Excellent';
	averagePaymentDays: number;
	talentRetentionPercent: number;
	projectCompletionPercent: number;
	averageTimesheetApprovalDays: number;
}

/**
 * Company Dashboard Types
 * @userstory US-DASH-CO-01 — View dashboard context
 * @userstory US-DASH-CO-02 — Track onboarding journey
 * @userstory US-DASH-CO-03 — View timeline/story
 * @userstory US-DASH-CO-05 — View pending actions
 * @userstory US-DASH-CO-06 — View team overview
 * @userstory US-DASH-CO-07 — View financial overview
 * @userstory US-DASH-CO-08 — View project performance
 * @userstory US-DASH-CO-09 — Monitor burn rate
 * @userstory US-DASH-CO-10 — Track expiring contracts
 * @userstory US-DASH-CO-11 — Monitor timesheets
 * @userstory US-DASH-CO-12 — Track milestones
 */

export interface CompanyFinancials {
	budgetTotal: number;
	spentTotal: number;
	grossMarginPct: number;
	contractsActive: number;
	contractsExpiring: number;
	currency: string;
	asOf: string;
}

export interface CompanyProjectMilestone {
	title: string;
	dueOn: string;
}

export interface CompanyProjectItem {
	projectId: string;
	name: string;
	status: string;
	budget: number;
	spent: number;
	burnPct: number;
	marginPct: number;
	nextMilestone: CompanyProjectMilestone;
	sparkline: number[];
}

export interface CompanyProjectsData {
	period: string;
	items: CompanyProjectItem[];
}

export interface CompanyContractsData {
	active: number;
	expiringWithin: number;
	expiring: any[]; // Type can be expanded based on actual data
}

export interface CompanyTimesheetsData {
	pendingApproval: number;
	lateSubmissions: number;
	billableHours: number;
	period: string;
}

export interface CompanyMilestoneFunnel {
	planned: number;
	inProgress: number;
	completed: number;
	approved: number;
}

export interface CompanyRiskMilestone {
	milestoneId: string;
	projectId: string;
	title: string;
	dueOn: string;
	daysOverdue: number;
}

export interface CompanyMilestonesData {
	funnel: CompanyMilestoneFunnel;
	atRisk: CompanyRiskMilestone[];
}

/**
 * Overview of freelance user's contracts
 */
export interface FreelanceContractsOverview {
	activeContracts: number;
	expiringContracts: number;
	pendingContracts: number;
}

/**
 * Summary of milestone status for the freelance user
 */
export interface FreelanceMilestoneDashboard {
	inProgress: number;
	availableToPick: number;
	dueSoon: number;
}

/**
 * Summary of timesheet status for the freelance user
 */
export interface FreelanceTimesheetStatus {
	pending: number;
	approved: number;
	hasCurrentMonth: boolean;
}

/**
 * Performance KPI summary for the freelance user
 */
export interface FreelancePerformance {
	total: number;
	completed: number;
	approved: number;
	completionRate: number;
	approvalRate: number;
}

/**
 * Company project budget overview data
 */
export interface CompanyProjectBudgetOverview {
	projectId: string;
	allocatedBudget: number;
	totalSpent: number;
	remainingBudget: number;
	remainingBudgetPct: number;
}

/**
 * Company project monthly burn rate snapshot
 */
export interface CompanyProjectBurnRate {
	organizationId: string;
	projectId: string;
	year: number;
	month: number;
	budget: number;
	timesheetSpent: number;
	milestoneSpent: number;
	totalSpent: number;
	spentToDate: number;
	remainingBudget: number;
}

/**
 * Company project payment due per month
 */
export interface CompanyProjectPaymentDue {
	organizationId: string;
	projectId: string;
	year: number;
	month: number;
	projectExternalCollaboratorId: string;
	talentUserId: string;
	freelanceFullName: string;
	contractId: string;
	contractHourlyRate: number;
	contractPaymentTermsDays: number;
	timesheetHours: number;
	milestoneHours: number;
	totalHours: number;
	amountToPay: number;
	paymentDueDate: string;
}

/**
 * Company talent profile
 */
export interface CompanyTalent {
	talentUserId: string;
	firstName: string;
	lastName: string;
	currentTitle?: string;
	hourlyRate: number;
	contractRate: number;
	isActive: boolean;
	availableFrom?: string;
	location?: string;
	isVisibleOnline: boolean;
	personalStatement?: string;
	skillsJson?: string;
	tagsJson?: string;
	contractsCount: number;
	lastContractStart?: string;
	lastContractEnd?: string;
	// Media fields
	profilePictureUrl?: string;
	profilePictureThumbnailUrl?: string;
	introductionVideoUrl?: string;
	introductionVideoThumbnailUrl?: string;
	introductionVideoDurationSeconds?: number;
}

const dashboardService = {
	/**
	 * Get freelance user's financial dashboard data
	 */
	async getFreelanceFinancials(): Promise<FreelanceFinancials> {
		try {
			const response = await apiClient.get<FreelanceFinancials>(
				'/api/talentusers/me/dashboard/earnings',
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch financial data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get freelance user's revenue data
	 */
	async getFreelanceRevenue(): Promise<FreelanceRevenue> {
		try {
			const response = await apiClient.get<FreelanceRevenue>(
				'/api/talentusers/me/dashboard/revenue',
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch revenue data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get freelance user's milestones
	 */
	async getFreelanceMilestones(): Promise<FreelanceMilestone[]> {
		try {
			const response = await apiClient.get<FreelanceMilestone[]>(
				'/api/talentusers/me/dashboard/milestones',
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch milestones';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get freelance user's timesheets
	 */
	async getFreelanceTimesheets(): Promise<FreelanceTimesheet[]> {
		try {
			const response = await apiClient.get<FreelanceTimesheet[]>(
				'/api/talentusers/me/dashboard/timesheets',
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch timesheets';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company financial dashboard data
	 */
	async getCompanyFinancials(organizationId: string): Promise<CompanyFinancials> {
		try {
			const response = await apiClient.get<CompanyFinancials>(
				`/api/organizations/${organizationId}/dashboard/financials`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch financial data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company projects dashboard data
	 */
	async getCompanyProjects(organizationId: string): Promise<CompanyProjectsData> {
		try {
			const response = await apiClient.get<CompanyProjectsData>(
				`/api/organizations/${organizationId}/dashboard/projects`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch projects data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company contracts dashboard data
	 */
	async getCompanyContracts(organizationId: string): Promise<CompanyContractsData> {
		try {
			const response = await apiClient.get<CompanyContractsData>(
				`/api/organizations/${organizationId}/dashboard/contracts`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch contracts data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company timesheets dashboard data
	 */
	async getCompanyTimesheets(organizationId: string): Promise<CompanyTimesheetsData> {
		try {
			const response = await apiClient.get<CompanyTimesheetsData>(
				`/api/organizations/${organizationId}/dashboard/timesheets`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch timesheets data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company milestones dashboard data
	 */
	async getCompanyMilestones(organizationId: string): Promise<CompanyMilestonesData> {
		try {
			const response = await apiClient.get<CompanyMilestonesData>(
				`/api/organizations/${organizationId}/dashboard/milestones`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch milestones data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get freelance user's contract overview
	 */
	async getFreelanceContracts(): Promise<FreelanceContractsOverview> {
		try {
			const response = await apiClient.get<FreelanceContractsOverview>(
				'/api/talentusers/me/dashboard/contracts',
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch contract data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get freelance user's milestone dashboard summary
	 */
	async getFreelanceMilestoneDashboard(): Promise<FreelanceMilestoneDashboard> {
		try {
			const response = await apiClient.get<FreelanceMilestoneDashboard>(
				'/api/talentusers/me/dashboard/milestones',
			);
			return response.data;
		} catch (error: any) {
			const errorMessage =
				error.response?.data?.message || 'Failed to fetch milestone dashboard';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get freelance user's timesheet status summary
	 */
	async getFreelanceTimesheetStatus(): Promise<FreelanceTimesheetStatus> {
		try {
			const response = await apiClient.get<FreelanceTimesheetStatus>(
				'/api/talentusers/me/dashboard/timesheets',
			);
			return response.data;
		} catch (error: any) {
			const errorMessage =
				error.response?.data?.message || 'Failed to fetch timesheet status';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get freelance user's performance summary
	 */
	async getFreelancePerformance(): Promise<FreelancePerformance> {
		try {
			const response = await apiClient.get<FreelancePerformance>(
				'/api/talentusers/me/dashboard/performance',
			);
			return response.data;
		} catch (error: any) {
			const errorMessage =
				error.response?.data?.message || 'Failed to fetch performance data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company project budget overview
	 */
	async getCompanyBudgetOverview(
		organizationId: string,
	): Promise<CompanyProjectBudgetOverview[]> {
		try {
			const response = await apiClient.get<CompanyProjectBudgetOverview[]>(
				`/api/organizations/${organizationId}/dashboard/budget`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch budget overview';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company project burn rates by month
	 */
	async getCompanyBurnRates(organizationId: string): Promise<CompanyProjectBurnRate[]> {
		try {
			const response = await apiClient.get<CompanyProjectBurnRate[]>(
				`/api/organizations/${organizationId}/dashboard/burnrate`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch burn rate data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get upcoming payment obligations per project and month
	 */
	async getCompanyPaymentsDue(organizationId: string): Promise<CompanyProjectPaymentDue[]> {
		try {
			const response = await apiClient.get<CompanyProjectPaymentDue[]>(
				`/api/organizations/${organizationId}/dashboard/paymentsdue`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage =
				error.response?.data?.message || 'Failed to fetch payments due data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get all talents linked to the organization (current and past)
	 */
	async getCompanyTalents(organizationId: string): Promise<CompanyTalent[]> {
		try {
			const response = await apiClient.get<CompanyTalent[]>(
				`/api/organizations/${organizationId}/dashboard/talents`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch talents';
			throw new Error(errorMessage);
		}
	},

	// =====================================================================
	// NEW ONBOARDING & GOAL METHODS
	// =====================================================================

	/**
	 * Get freelance user's onboarding status
	 */
	async getTalentOnboardingStatus(): Promise<TalentOnboardingStatus> {
		try {
			const response = await apiClient.get<TalentOnboardingStatus>(
				'/api/talentusers/me/dashboard/onboarding',
			);
			return response.data;
		} catch (error: any) {
			const errorMessage =
				error.response?.data?.message || 'Failed to fetch onboarding status';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get freelance user's yearly goal data
	 */
	async getTalentYearlyGoal(year?: number): Promise<TalentYearlyGoal> {
		try {
			const params = year ? `?year=${year}` : '';
			const response = await apiClient.get<TalentYearlyGoal>(
				`/api/talentusers/me/dashboard/yearly-goal${params}`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch yearly goal';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Set freelance user's yearly goal
	 */
	async setTalentYearlyGoal(amount: number): Promise<void> {
		try {
			await apiClient.put('/api/talentusers/me/dashboard/yearly-goal', { amount });
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to set yearly goal';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Set all freelance user's yearly goals at once
	 */
	async setAllTalentYearlyGoals(request: SetAllYearlyGoalsRequest): Promise<TalentYearlyGoal> {
		try {
			const response = await apiClient.put<TalentYearlyGoal>(
				'/api/talentusers/me/dashboard/yearly-goals',
				request,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to set yearly goals';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get freelance user's quick stats
	 */
	async getTalentQuickStats(): Promise<TalentQuickStats> {
		try {
			const response = await apiClient.get<TalentQuickStats>(
				'/api/talentusers/me/dashboard/quick-stats',
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch quick stats';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get freelance dashboard context for layout and experience level
	 */
	async getTalentDashboardContext(): Promise<TalentDashboardContext> {
		try {
			const response = await apiClient.get<TalentDashboardContext>(
				'/api/talentusers/me/dashboard/context',
			);
			return response.data;
		} catch (error: any) {
			const errorMessage =
				error.response?.data?.message || 'Failed to fetch dashboard context';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company onboarding status
	 */
	async getCompanyOnboardingStatus(organizationId: string): Promise<CompanyOnboardingStatus> {
		try {
			const response = await apiClient.get<CompanyOnboardingStatus>(
				`/api/organizations/${organizationId}/dashboard/onboarding`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage =
				error.response?.data?.message || 'Failed to fetch onboarding status';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company team overview
	 * @param viewMode Optional view mode: 'admin' | 'manager' | 'myprojects'
	 */
	async getCompanyTeamOverview(
		organizationId: string,
		viewMode?: string,
	): Promise<CompanyTeamOverview> {
		try {
			const params = viewMode ? { viewMode } : {};
			const response = await apiClient.get<CompanyTeamOverview>(
				`/api/organizations/${organizationId}/dashboard/team-overview`,
				{ params },
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch team overview';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company pending actions
	 * @param viewMode Optional view mode: 'admin' | 'manager' | 'myprojects'
	 */
	async getCompanyPendingActions(
		organizationId: string,
		viewMode?: string,
	): Promise<CompanyPendingActions> {
		try {
			const params = viewMode ? { viewMode } : {};
			const response = await apiClient.get<CompanyPendingActions>(
				`/api/organizations/${organizationId}/dashboard/pending-actions`,
				{ params },
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch pending actions';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company quick stats
	 * @param viewMode Optional view mode: 'admin' | 'manager' | 'myprojects'
	 */
	async getCompanyQuickStats(
		organizationId: string,
		viewMode?: string,
	): Promise<CompanyQuickStats> {
		try {
			const params = viewMode ? { viewMode } : {};
			const response = await apiClient.get<CompanyQuickStats>(
				`/api/organizations/${organizationId}/dashboard/quick-stats`,
				{ params },
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch quick stats';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company dashboard context for layout and experience level
	 */
	async getCompanyDashboardContext(organizationId: string): Promise<CompanyDashboardContext> {
		try {
			const response = await apiClient.get<CompanyDashboardContext>(
				`/api/organizations/${organizationId}/dashboard/context`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage =
				error.response?.data?.message || 'Failed to fetch dashboard context';
			throw new Error(errorMessage);
		}
	},

	// =====================================================================
	// COMPANY ONBOARDING JOURNEY & EMPLOYER SCORE METHODS
	// =====================================================================

	/**
	 * Get company onboarding journey status (10 steps)
	 */
	async getCompanyOnboardingJourney(organizationId: string): Promise<CompanyOnboardingJourney> {
		try {
			const response = await apiClient.get<CompanyOnboardingJourney>(
				`/api/organizations/${organizationId}/dashboard/onboarding-journey`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage =
				error.response?.data?.message || 'Failed to fetch onboarding journey';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company timeline (story)
	 */
	async getCompanyTimeline(organizationId: string): Promise<CompanyTimeline> {
		try {
			const response = await apiClient.get<CompanyTimeline>(
				`/api/organizations/${organizationId}/dashboard/timeline`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch timeline';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Get company employer score
	 */
	async getCompanyEmployerScore(organizationId: string): Promise<CompanyEmployerScore> {
		try {
			const response = await apiClient.get<CompanyEmployerScore>(
				`/api/organizations/${organizationId}/dashboard/employer-score`,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch employer score';
			throw new Error(errorMessage);
		}
	},
};

export default dashboardService;
